import React, { createContext, useContext, useEffect, useState } from 'react';
import '../../../../css/common/chat/AdminSenderBadge.css';

// A surrounding status avatar can supply the content for this single badge slot.
export const AccountAvatarBadgeContext = createContext(/** @type {React.ReactNode} */ (null));

const onKeyActivate = (event, action) => {
  if (event.key !== 'Enter' && event.key !== ' ') return;
  event.preventDefault();
  action?.(event);
};

const extractBackgroundImageUrl = (style) => {
  const backgroundImage = String(style?.backgroundImage || '').trim();
  if (!backgroundImage || backgroundImage === 'none') return '';
  const match = backgroundImage.match(/^url\((['"]?)(.*?)\1\)$/i);
  return match?.[2] || '';
};

export default function AccountAvatarTrigger({
  className = '',
  title = '',
  imageUrl = '',
  imageAlt = '',
  style = undefined,
  fallback = /** @type {React.ReactNode} */ (null),
  onClick = /** @type {((event: React.MouseEvent | React.KeyboardEvent) => void) | null} */ (null),
  clickableClassName = '',
  stopPropagation = false,
  imageLoading = 'lazy',
}) {
  const statusBadge = useContext(AccountAvatarBadgeContext);
  const showIdentity = className.split(/\s+/).includes('is-admin-identity');
  const [imageFailed, setImageFailed] = useState(false);
  const clickable = typeof onClick === 'function';
  const mergedClassName = [className, clickable ? clickableClassName : '']
    .map((token) => String(token || '').trim())
    .filter(Boolean)
    .join(' ');

  const activate = (event) => {
    if (!clickable) return;
    if (stopPropagation && event?.stopPropagation) event.stopPropagation();
    onClick(event);
  };
  const backgroundImageUrl = !imageUrl ? extractBackgroundImageUrl(style) : '';
  const resolvedImageUrl = imageUrl || backgroundImageUrl;
  const shouldShowImage = Boolean(resolvedImageUrl && !imageFailed);
  const containerStyle = backgroundImageUrl
    ? {
        ...style,
        backgroundImage: undefined,
        backgroundSize: undefined,
        backgroundPosition: undefined,
      }
    : style;

  useEffect(() => {
    setImageFailed(false);
  }, [resolvedImageUrl]);

  return (
    <div
      className={mergedClassName}
      title={statusBadge ? undefined : title || undefined}
      aria-label={clickable ? title || imageAlt || undefined : undefined}
      style={containerStyle}
      role={clickable ? 'button' : undefined}
      tabIndex={clickable ? 0 : undefined}
      onClick={clickable ? activate : undefined}
      onKeyDown={clickable ? (event) => onKeyActivate(event, activate) : undefined}
    >
      {shouldShowImage ? (
        <img
          src={resolvedImageUrl}
          alt={imageAlt || ''}
          loading={imageLoading}
          decoding="async"
          onError={() => setImageFailed(true)}
          style={{
            width: '100%',
            height: '100%',
            display: 'block',
            objectFit: 'cover',
            borderRadius: 'inherit',
          }}
        />
      ) : fallback}
      {statusBadge || showIdentity ? (
        <span className="chat-avatar-badge">
          {statusBadge || <span className="admin-sender-badge__mark" aria-hidden="true" />}
        </span>
      ) : null}
    </div>
  );
}
